import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, AlertTriangle, Clock, Send } from 'lucide-react'
import { listAdminApprovalOverdue, sendAdminApprovalMahnung } from '@/api/kostenuebernahme'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'

/**
 * Phase J — Superadmin-Inbox "Approval überfällig".
 *
 * Listet Kostenübernahmen in <c>pending_admin_approval</c>, deren Anfrage
 * älter als 60 Tage ist (Spec §10 Eskalation). Der Notifier hat nach 30
 * Tagen bereits eine Mahn-Mail an die Firma-Admins versandt; nach weiteren
 * 30 Tagen wird der Vorgang hier sichtbar zur manuellen Behandlung durch
 * den Superadmin (z.B. manuelle Mahnung, Storno, oder Eskalation außerhalb
 * des Systems).
 */
export default function AdminApprovalOverduePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [busyId, setBusyId] = useState<number | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-approval-overdue'],
    queryFn: () => listAdminApprovalOverdue({ skip: 0, limit: 200 }),
  })

  const items = data?.items ?? []

  async function handleMahnung(id: number) {
    if (busyId) return
    if (!window.confirm('Mahn-Mail jetzt manuell an alle Firma-Admins versenden?')) return
    setBusyId(id)
    try {
      await sendAdminApprovalMahnung(id)
      await qc.invalidateQueries({ queryKey: ['admin-approval-overdue'] })
    } catch (e) {
      window.alert(`Fehler: ${(e as Error).message}`)
    } finally {
      setBusyId(null)
    }
  }

  function daysAgo(iso: string | null | undefined): number | null {
    if (!iso) return null
    const diff = Date.now() - new Date(iso).getTime()
    return Math.floor(diff / (1000 * 60 * 60 * 24))
  }

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg px-6 py-5 mb-5">
        <button
          onClick={() => navigate('/superadmin/kostenuebernahmen')}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-2"
        >
          <ArrowLeft size={14} />
          Zurück zur Übersicht
        </button>
        <h1 className="text-[1.35rem] font-bold text-gray-900 tracking-tight flex items-center gap-2">
          <Clock size={20} className="text-fom-500" />
          Admin-Approval überfällig
        </h1>
        <p className="text-gray-500 text-[0.78rem] mt-0.5">
          Vorgänge in <code>pending_admin_approval</code> mit Anfrage älter als 60 Tage
          (Spec §10). Der Notifier mahnt automatisch nach 30 Tagen.
        </p>
      </div>

      {/* Hinweis-Banner */}
      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 flex gap-3 items-start">
        <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-red-800">
          <b>Diese Vorgänge warten länger als 60 Tage auf die Bestätigung des Firma-Admins.</b>
          <p className="mt-1">
            Bitte manuell prüfen: Firma-Admin direkt kontaktieren, neuen Admin im
            CRM aktivieren, Vorgang stornieren oder selbst weiterführen. Eine
            erneute Mahn-Mail kann pro Zeile ausgelöst werden.
          </p>
        </div>
      </div>

      {/* Content */}
      {isLoading && <Spinner className="mt-10" />}
      {error && <ErrorAlert message={(error as Error).message} onRetry={refetch} />}

      {!isLoading && !error && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="text-xs text-gray-400 px-4 py-2 border-b border-gray-100">
            {items.length} {items.length === 1 ? 'Eintrag' : 'Einträge'}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Studierende/r</th>
                <th className="px-4 py-3 text-left">Zuzahler-Firma</th>
                <th className="px-4 py-3 text-left">AP</th>
                <th className="px-4 py-3 text-left">Anfrage seit</th>
                <th className="px-4 py-3 text-left">Mahnung</th>
                <th className="px-4 py-3 text-right">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => {
                const days = daysAgo(item.AdminApprovalRequestedAt)
                const mahnungDays = daysAgo(item.AdminApprovalMahnungSentAt)
                return (
                  <tr key={item.Id} className="hover:bg-gray-50 transition-colors">
                    <td
                      className="px-4 py-3 font-medium text-gray-900 cursor-pointer"
                      onClick={() => navigate(`/superadmin/kostenuebernahmen/${item.Id}`)}
                    >
                      {[item.StudiVorname, item.StudiNachname].filter(Boolean).join(' ') || '–'}
                      {item.StudiMatrikel && (
                        <div className="text-xs text-gray-400 font-mono mt-0.5">
                          {item.StudiMatrikel}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {item.ZuzahlerFirma ?? '–'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="font-medium">{item.ZuzahlerApName ?? '–'}</div>
                      {item.ZuzahlerApEmail && (
                        <div className="text-xs text-gray-500 mt-0.5">{item.ZuzahlerApEmail}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-red-700 font-semibold">
                      {days !== null ? `${days} Tage` : '–'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {mahnungDays !== null
                        ? `vor ${mahnungDays} Tagen`
                        : <span className="text-gray-400 italic">noch keine</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleMahnung(item.Id)}
                        disabled={busyId === item.Id}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-fom-200 text-fom-700 bg-fom-50 hover:bg-fom-100 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Send size={12} />
                        {busyId === item.Id ? 'Sende…' : 'Mahn-Mail'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {items.length === 0 && (
            <div className="text-center text-gray-400 py-12 text-sm">
              Keine überfälligen Vorgänge.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
